/*
 * aura-omnibox.js: search box with suggestions (spec 05, baseline on Shopify predictive search).
 * ARIA 1.2 combobox: arrow keys move through options, Enter opens, Esc clears then closes.
 * "/" or Cmd/Ctrl+K focuses it. Requests wait 80ms after the last keystroke and cancel stale ones.
 * Ask Aura and photo search are not part of the baseline.
 */
(() => {
  const cfg = (window.aura && window.aura.config) || {};
  const track = (n, p) => window.aura && window.aura.track(n, p);

  class AuraOmnibox extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('.omnibox__panel');
      this.status = this.querySelector('[data-omnibox-status]');
      this.form = this.querySelector('form');
      this.active = -1;
      this.input.addEventListener('input', () => this.queue());
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.input.addEventListener('focus', () => track('aura_search_focused', { surface: 'header' }));
      this.form.addEventListener('submit', () => track('aura_search_submitted', { query: this.input.value, input: 'typed' }));
      this.panel.addEventListener('click', (e) => {
        const a = e.target.closest('a[data-suggest-group]');
        if (a) track('aura_search_suggestion_selected', { group: a.dataset.suggestGroup, position: this.options().indexOf(a.closest('[role="option"]')) + 1, handle: a.getAttribute('href') });
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
      document.addEventListener('keydown', (e) => {
        const typing = /INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName) || document.activeElement.isContentEditable;
        if ((e.key === '/' && !typing) || (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey))) {
          if (this.offsetParent === null) return;
          e.preventDefault();
          this.input.focus();
        }
      });
    }
    queue() {
      clearTimeout(this.t);
      const q = this.input.value.trim();
      if (!q) { this.close(); return; }
      this.t = setTimeout(() => this.fetch(q), 80);
    }
    async fetch(q) {
      this.controller?.abort();
      this.controller = new AbortController();
      const t0 = performance.now();
      const url = `${cfg.routes.predictive_search}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article&resources[limit]=6&resources[limit_scope]=each&section_id=predictive-search`;
      try {
        const html = await (await fetch(url, { signal: this.controller.signal })).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const box = doc.querySelector('.suggest');
        if (!box) return;
        this.panel.innerHTML = box.outerHTML;
        this.open();
        const count = Number(box.dataset.suggestCount || 0);
        this.status.textContent = count ? `${count}` : '';
        track('aura_search_suggestion_shown', { query: q, results: count, ms: Math.round(performance.now() - t0) });
        if (!count) track('aura_search_zero_results', { query: q, fallback_shown: true });
      } catch (e) {
        if (e.name !== 'AbortError') this.close();
      }
    }
    options() { return [...this.panel.querySelectorAll('[role="option"]')]; }
    open() { this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true'); this.setActive(-1); }
    close() { this.panel.hidden = true; this.input.setAttribute('aria-expanded', 'false'); this.input.removeAttribute('aria-activedescendant'); this.active = -1; }
    setActive(i) {
      const opts = this.options();
      opts.forEach((o) => o.setAttribute('aria-selected', 'false'));
      this.active = i;
      if (i < 0 || !opts[i]) { this.input.removeAttribute('aria-activedescendant'); return; }
      opts[i].setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', opts[i].id);
      opts[i].scrollIntoView({ block: 'nearest' });
    }
    onKey(e) {
      const opts = this.options();
      if (e.key === 'ArrowDown' && !this.panel.hidden) { e.preventDefault(); this.setActive((this.active + 1) % opts.length); }
      else if (e.key === 'ArrowUp' && !this.panel.hidden) { e.preventDefault(); this.setActive(this.active <= 0 ? opts.length - 1 : this.active - 1); }
      else if (e.key === 'Enter' && this.active >= 0 && opts[this.active]) { e.preventDefault(); opts[this.active].querySelector('a').click(); }
      else if (e.key === 'Escape') { if (this.input.value && this.panel.hidden) this.input.value = ''; this.close(); }
    }
  }
  if (!customElements.get('aura-omnibox')) customElements.define('aura-omnibox', AuraOmnibox);
})();
